/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { VIRTUALIZED_LIST_PROPS } from '../../shared/ui/virtualizedListProps';
import { fetchStoreReviewsForBranch, fetchStoreReviewStats } from '../infra/storeReviewsRepo';
import {
  STORE_REVIEWS_PAGE_SIZE,
  type StoreReviewFilter,
  type StoreReviewRow,
  type StoreReviewStats,
} from '../domain/storeReviewTypes';
import { ticketColors } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing';

function starsLabel(score: number): string {
  return `${'★'.repeat(Math.max(0, Math.min(5, score)))}${'☆'.repeat(Math.max(0, 5 - score))}`;
}

function formatDate(iso: string): string {
  return formatLocalDateTime(iso);
}

export function StoreReviewsScreen() {
  const navigation =
    useNavigation<NativeStackNavigationProp<{ StoreReviewDetail: { reviewId: string } }>>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const canView = useModulePermission('BranchSettings', 'view');
  const { layoutWide } = useContentLayout();

  const [reviews, setReviews] = useState<StoreReviewRow[]>([]);
  const [stats, setStats] = useState<StoreReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<StoreReviewFilter>('all');
  const [pageIndex, setPageIndex] = useState(0);

  const load = useCallback(async () => {
    if (!branchId || !canView) return;
    setLoading(true);
    setError(false);
    setPageIndex(0);
    try {
      const [data, statsData] = await Promise.all([
        fetchStoreReviewsForBranch(branchId, filter, 0),
        fetchStoreReviewStats(branchId),
      ]);
      setReviews(data);
      setHasMore(data.length === STORE_REVIEWS_PAGE_SIZE);
      setStats(statsData);
    } catch (e) {
      console.error('StoreReviewsScreen load', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [branchId, canView, filter]);

  useScreenLoadShell('StoreReviews', load, { minFocusIntervalMs: 60_000 });

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || loading || !branchId || !canView) return;
    setLoadingMore(true);
    try {
      const nextPage = pageIndex + 1;
      const data = await fetchStoreReviewsForBranch(branchId, filter, nextPage);
      setReviews((prev) => [...prev, ...data]);
      setHasMore(data.length === STORE_REVIEWS_PAGE_SIZE);
      setPageIndex(nextPage);
    } catch (e) {
      console.error('StoreReviewsScreen loadMore', e);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, loading, branchId, canView, pageIndex, filter]);

  const handleFilterChange = useCallback((next: StoreReviewFilter) => {
    setFilter(next);
  }, []);

  const containerStyle = layoutWide
    ? [styles.container, styles.containerWide]
    : [styles.container, styles.containerCompact];

  if (!canView) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState
          variant="nest"
          kicker="Reseñas"
          title="No podés ver Reseñas"
          subtitle="Pedile al dueño que te dé permiso."
          testID="store-reviews.no-permission"
        />
      </ShellContentFrame>
    );
  }

  if (!branchId) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState
          variant="nest"
          kicker="Reseñas"
          title="Elegí una sucursal"
          subtitle="Abrí el selector de sucursal en la barra superior."
          testID="store-reviews.no-branch"
        />
      </ShellContentFrame>
    );
  }

  // Los KPI tiles son el único control de filtro — antes una barra segmentada
  // repetía los mismos segmentos y consumía espacio doble (#1462).
  const kpiItems: TicketKpiItem[] = stats
    ? [
        {
          label: 'Todas',
          value: String(stats.total),
          onPress: () => handleFilterChange('all'),
          selected: filter === 'all',
          testID: 'store-reviews.kpi.total',
        },
        {
          label: 'Promedio',
          // Sin reseñas no hay rating: "—" neutro, nunca "0.0★" en rojo.
          value: stats.total > 0 ? `${stats.avg_score.toFixed(1)}★` : '—',
          tone:
            stats.total === 0
              ? 'default'
              : stats.avg_score >= 4
                ? 'ok'
                : stats.avg_score >= 3
                  ? 'warn'
                  : 'bad',
          testID: 'store-reviews.kpi.avg',
        },
        {
          label: 'Sin responder',
          value: String(stats.unanswered),
          tone: stats.unanswered > 0 ? 'warn' : 'default',
          onPress: () => handleFilterChange('unanswered'),
          selected: filter === 'unanswered',
          testID: 'store-reviews.kpi.unanswered',
        },
        {
          label: '2★ o menos',
          value: String(stats.low),
          tone: stats.low > 0 ? 'bad' : 'default',
          onPress: () => handleFilterChange('low'),
          selected: filter === 'low',
          testID: 'store-reviews.kpi.low',
        },
      ]
    : [];

  return (
    <ShellContentFrame style={containerStyle}>
      <TicketModuleHeader
        kicker="Reseñas"
        title="Lo que dicen tus clientes"
        meta="Reseñas del local y tus respuestas"
        testID="store-reviews.header"
      />

      {kpiItems.length > 0 ? (
        <View style={styles.kpiWrap}>
          <TicketKpiStrip items={kpiItems} variant="soft" layout={layoutWide ? 'desk' : 'mobile'} />
        </View>
      ) : null}

      {error && !loading ? (
        <ModuleEmptyState
          variant="nest"
          kicker="Reseñas"
          title="No se pudieron cargar las reseñas"
          subtitle="Revisá tu conexión y probá de nuevo."
          primaryAction={{ label: 'Reintentar', onPress: () => void load() }}
        />
      ) : (
        <FlatList
          data={reviews}
          keyExtractor={(item) => item.id}
          renderItem={({ item, index }) => {
            const unanswered = !item.merchant_reply;
            return (
              <TicketListRow
                title={item.comment || 'Sin comentario'}
                subtitle={formatDate(item.created_at)}
                meta={item.customer_id ? 'Cliente verificado' : 'Cliente anónimo'}
                value={starsLabel(item.score)}
                statusLabel={unanswered ? 'Sin responder' : 'Respondida'}
                statusTone={unanswered ? 'warn' : 'ok'}
                chipLabel={unanswered ? 'Pendiente' : null}
                chipTone={unanswered ? 'warn' : 'default'}
                onPress={() => navigation.navigate('StoreReviewDetail', { reviewId: item.id })}
                showChevron
                group={index === 0 ? 'solo' : index === reviews.length - 1 ? 'groupedLast' : 'grouped'}
                testID="store-reviews.card"
              />
            );
          }}
          refreshControl={
            <RefreshControl
              refreshing={loading}
              onRefresh={load}
              colors={[ticketColors.ink2]}
              tintColor={ticketColors.ink2}
            />
          }
          onEndReached={() => void loadMore()}
          onEndReachedThreshold={0.3}
          contentContainerStyle={reviews.length === 0 ? styles.emptyContainer : undefined}
          ListEmptyComponent={
            !loading ? (
              <ModuleEmptyState
                variant="nest"
                kicker="Reseñas"
                title="No hay reseñas"
                subtitle={
                  filter === 'unanswered'
                    ? 'Todas las reseñas están respondidas.'
                    : filter === 'low'
                      ? 'No hay reseñas con 2★ o menos.'
                      : 'Cuando los clientes dejen reseñas, van a aparecer acá.'
                }
                testID="store-reviews.empty"
              />
            ) : null
          }
          ListFooterComponent={
            hasMore && reviews.length > 0 ? (
              <TicketDeskFooter
                pageIndex={pageIndex}
                pageSize={STORE_REVIEWS_PAGE_SIZE}
                productCount={reviews.length}
                totalCount={null}
                hasMore={hasMore}
                loading={loadingMore}
                onPreviousPage={() => {}}
                onNextPage={() => void loadMore()}
                embedded
              />
            ) : null
          }
          {...VIRTUALIZED_LIST_PROPS}
        />
      )}
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    ...ticketSurfaces.screen,
  },
  containerWide: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  containerCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  kpiWrap: {
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  emptyContainer: {
    flex: 1,
  },
});
